import React, { useRef, useImperativeHandle, forwardRef } from "react";
import Slider, { Settings } from "react-slick";
import styled from "styled-components";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

const CarouselContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;

  .slick-dots {
    position: relative;
    bottom: auto;
    width: 100%;
    margin-bottom: 15px;
    padding: 0;

    li button:before {
      font-size: 12px;
      color: #000;
      opacity: 0.3;
    }

    li.slick-active button:before {
      color: #000;
      opacity: 1;
    }
  }

  .slick-arrow {
    z-index: 2;
    width: 40px;
    height: 40px;

    &:before {
      font-size: 40px;
      color: #000;
    }

    &.slick-prev {
      left: 10px;
    }

    &.slick-next {
      right: 10px;
    }
  }

  @media (max-width: 768px) {
    .slick-arrow {
      display: none !important;
    }

    width: 100vw;
    margin-left: calc(-50vw + 50%);

    .slick-list {
      margin: 0;
      padding: 0;
    }

    .slick-slide {
      padding: 0;
    }

    .slick-track {
      display: flex !important;
    }
  }
`;

const MemberIndicator = styled.div`
  text-align: center;
  margin-bottom: 10px;
  font-size: 18px;
  font-weight: 600;
  color: #333;
  min-height: 25px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    margin-bottom: 5px;
    font-size: 16px;
  }
`;

export interface CarouselRef {
  goToSlide: (index: number) => void;
  getCurrentSlide: () => number;
}

interface CarouselWrapperProps {
  children: React.ReactNode[];
  initialSlide?: number;
  memberNames?: string[];
  onSlideChange?: (currentSlide: number) => void;
  showDots?: boolean;
  showArrows?: boolean;
}

const CarouselWrapper = forwardRef<CarouselRef, CarouselWrapperProps>(
  (
    {
      children,
      initialSlide = 0,
      memberNames = [],
      onSlideChange,
      showDots = true,
      showArrows = true,
    },
    ref,
  ) => {
    const sliderRef = useRef<Slider>(null);
    const [currentSlide, setCurrentSlide] = React.useState(initialSlide);

    // Expose methods via ref
    useImperativeHandle(ref, () => ({
      goToSlide: (index: number) => {
        if (sliderRef.current) {
          sliderRef.current.slickGoTo(index);
        }
      },
      getCurrentSlide: () => currentSlide,
    }));

    const settings: Settings = {
      dots: showDots && children.length > 1,
      infinite: false,
      slidesToShow: 1,
      slidesToScroll: 1,
      initialSlide,
      centerMode: true,
      centerPadding: "30px",
      arrows: showArrows && children.length > 1,
      autoplay: false,
      beforeChange: (_oldIndex: number, newIndex: number) => {
        setCurrentSlide(newIndex);
        onSlideChange?.(newIndex);
      },
      responsive: [
        {
          breakpoint: 768,
          settings: {
            arrows: false,
            dots: showDots && children.length > 1,
            centerMode: true,
            centerPadding: "100px",
          },
        },
      ],
    };

    // If only one slide, don't show carousel controls
    if (children.length <= 1) {
      return (
        <CarouselContainer>
          {memberNames.length > 0 && (
            <MemberIndicator>{memberNames[0]}</MemberIndicator>
          )}
          {children[0]}
        </CarouselContainer>
      );
    }

    return (
      <CarouselContainer>
        {memberNames.length > 0 && (
          <MemberIndicator>{memberNames[currentSlide] || ""}</MemberIndicator>
        )}
        <Slider ref={sliderRef} {...settings}>
          {children}
        </Slider>
      </CarouselContainer>
    );
  },
);

CarouselWrapper.displayName = "CarouselWrapper";

export default CarouselWrapper;
